/**
 * A custom element that applies a discount to the cart.
 */
class CartDiscount extends HTMLElement {
    constructor() {
        super();
        this.cartElement = null;
        this.cartSectionId = null;
        this.form = null;
        this.quickApplyButton = null;
        this.discountCodes = [];
        this.discountCodesObserver = null;
    }

    #validateSetup() {
        this.cartElement = this.closest('cart-form');
        this.cartSectionId = this.cartElement?.dataset?.sectionId;
        this.form = this.querySelector('form');
        this.quickApplyButton = this.querySelector('[data-quick-apply-discount]');

        if (!this.cartElement || !this.cartSectionId) {
            console.error('CartDiscount: Cart form element or section ID not found');
            return false;
        }

        if (!this.form && !this.quickApplyButton) {
            console.error('CartDiscount: Discount form or quick apply button not found');
            return false;
        }

        return true;
    }

    connectedCallback() {
        if (!this.#validateSetup()) return;

        this.discountCodes = [...this.querySelectorAll('[data-remove-discount]')];

        if (this.form) {
            this.form.addEventListener('submit', this.applyDiscount);
        }

        if (this.quickApplyButton) {
            this.quickApplyButton.addEventListener('click', this.applyQuickDiscount);
        }

        this.discountCodes.forEach(code => code.addEventListener('click', this.removeDiscount));

        this.discountCodesObserver = new MutationObserver(() => {
            this.discountCodes = [...this.querySelectorAll('[data-remove-discount]')];
            this.discountCodes.forEach(code => code.addEventListener('click', this.removeDiscount));
        });
        this.discountCodesObserver.observe(this, { childList: true, subtree: true });
    }

    disconnectedCallback() {
        this.form?.removeEventListener('submit', this.applyDiscount);
        this.quickApplyButton?.removeEventListener('click', this.applyQuickDiscount);
        this.discountCodes.forEach(code => code.removeEventListener('click', this.removeDiscount));
        this.discountCodesObserver?.disconnect();
    }

    #cartUpdateBody(discountValue) {
        return JSON.stringify({
            discount: discountValue,
            sections: [this.cartSectionId],
            sections_url: window.routes?.cart_fetch_url || '/cart'
        });
    }

    #sectionHtml(data) {
        return data?.sections?.[this.cartSectionId] || Object.values(data?.sections || {})[0];
    }

    applyQuickDiscount = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const button = event.currentTarget;
        const discountCodeValue = button.dataset.discountCode?.trim();

        if (!discountCodeValue) {
            console.error('CartDiscount: Quick apply discount code not configured');
            return;
        }

        await this.#applyDiscountCode(discountCodeValue, button);
    };

    applyDiscount = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const form = event.currentTarget;
        const discountCodeInput = form.querySelector('input[name="discount"]');
        const submitButton = form.querySelector('[type="submit"]');

        if (!form || !(discountCodeInput instanceof HTMLInputElement)) {
            console.error('CartDiscount: Form or input element not found');
            return;
        }

        const discountCodeValue = discountCodeInput.value.trim();
        if (!discountCodeValue) {
            console.warn('CartDiscount: Empty discount code');
            return this.#showErrorMessage('Please enter a discount code');
        }

        await this.#applyDiscountCode(discountCodeValue, submitButton, () => {
            discountCodeInput.value = '';
        });
    };

    async #applyDiscountCode(discountCodeValue, triggerButton, onSuccess) {
        const existingDiscounts = this.#existingDiscounts();
        if (existingDiscounts.includes(discountCodeValue.toLowerCase())) {
            console.warn('CartDiscount: Code already applied:', discountCodeValue);
            return this.#showErrorMessage('This discount code is already applied');
        }

        this.#setLoadingState(triggerButton, true);
        this.#hideErrorMessage();

        try {
            existingDiscounts.push(discountCodeValue);
            const data = await Utility.Fetch.json(`${routes.cart_update_url}`, {
                ...Utility.Fetch.config(),
                body: this.#cartUpdateBody(existingDiscounts.join(','))
            });

            const sectionHtml = this.#sectionHtml(data);
            if (!sectionHtml) {
                throw new Error('Invalid cart section data in response');
            }

            const appliedDiscount = data.discount_codes?.find(
                discount => discount.code.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === false
            );

            if (appliedDiscount) {
                console.warn('CartDiscount: Code not applicable:', discountCodeValue);
                return this.#showErrorMessage('This discount code is not valid');
            }

            onSuccess?.();
            Utility.announceToScreenReader(`Discount code ${discountCodeValue} applied`);

            if (this.cartElement?._updateCartUI) {
                this.cartElement._updateCartUI(sectionHtml);
            } else {
                console.warn('CartDiscount: Cart update method not available');
            }

        } catch (error) {
            console.error('CartDiscount: Error applying discount:', error);
            Utility.handleError(error.message || 'Failed to apply discount code');
            this.#showErrorMessage('Unable to apply discount code. Please try again.');
        } finally {
            this.#setLoadingState(triggerButton, false);
        }
    }

    removeDiscount = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const pill = event.currentTarget.closest('[data-discount-code]');
        const discountCode = pill?.dataset?.discountCode?.toLowerCase();

        if (!(pill instanceof HTMLLIElement) || !discountCode) {
            console.error('CartDiscount: Invalid pill element or discount code');
            return;
        }

        const existingDiscounts = this.#existingDiscounts();
        const index = existingDiscounts.indexOf(discountCode);
        if (index === -1) {
            console.warn('CartDiscount: Code not found:', discountCode);
            return;
        }

        existingDiscounts.splice(index, 1);
        const removeButton = event.currentTarget;
        const originalDisabled = removeButton.disabled;
        removeButton.disabled = true;

        try {
            const data = await Utility.Fetch.json(`${routes.cart_update_url}`, {
                ...Utility.Fetch.config(),
                body: this.#cartUpdateBody(existingDiscounts.join(','))
            });

            const sectionHtml = this.#sectionHtml(data);
            if (!sectionHtml) {
                throw new Error('Invalid cart section data in response');
            }

            const discountInput = this.querySelector('input[name="discount"]');
            if (discountInput) discountInput.value = '';

            Utility.announceToScreenReader(`Discount code ${discountCode} removed`);

            if (this.cartElement?._updateCartUI) {
                this.cartElement._updateCartUI(sectionHtml);
            } else {
                console.warn('CartDiscount: Cart update method not available');
            }

        } catch (error) {
            console.error('CartDiscount: Error removing discount:', error);
            Utility.handleError(error.message || 'Failed to remove discount code');
            removeButton.disabled = originalDisabled;
        }
    };

    #existingDiscounts() {
        return [...this.querySelectorAll('[data-discount-code]')]
            .filter(pill => pill instanceof HTMLLIElement && pill.dataset.discountCode)
            .map(pill => pill.dataset.discountCode.toLowerCase());
    }

    #hideErrorMessage() {
        const errorElement = this.querySelector('[data-discount-error]');
        if (!errorElement) return;
        errorElement.style.display = 'none';
    }

    #showErrorMessage(message) {
        const errorElement = this.querySelector('[data-discount-error]');
        if (!errorElement) return console.warn('CartDiscount: Error element not found');

        const errorText = errorElement.querySelector('[data-error-text]');
        if (errorText) errorText.textContent = message;
        Utility.Animate.showMessage(errorElement);
        Utility.announceToScreenReader(message, 'assertive');
    }

    #setLoadingState(button, isLoading) {
        if (!button) return;
        button.disabled = isLoading;
        Utility.Animate.setLoading(button, isLoading);
    }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
